<template>
  <div>
    <v-sheet outlined rounded>
      <div class="text-subtitle-1 ml-3 mt-1">{{ $t('filter_by') }}</div>
      <v-row dense class="mx-3 mt-2">
        <v-col lg="3" md="6" cols="12">
          <v-autocomplete
            multiple
            small-chips
            deletable-chips
            hide-no-data
            hide-selected
            clearable
            v-model="searchCriteria.userIds"
            :items="userOptions"
            :loading="loadingUserOptions"
            @update:search-input="searchUsers"
            :label="$t('email')"
            prepend-inner-icon="mdi-email"/>
        </v-col>
        <v-col lg="3" md="6" cols="12">
          <vf-autocomplete
            small
            :label="$tc('customer', 2)"
            v-model="searchCriteria.customers"
            :items="customerOptions"
            prepend-inner-icon="mdi-office-building"/>
        </v-col>
        <v-col lg="3" md="6" cols="12">
          <v-select
            multiple
            clearable
            hide-details
            v-model="searchCriteria.methods"
            :items="methodOptions"
            :label="$t('http_method')"
            prepend-inner-icon="mdi-swap-horizontal"/>
        </v-col>
        <v-col lg="3" md="6" cols="12">
          <v-text-field
            clearable
            v-model="searchCriteria.endpoint"
            :label="$t('endpoint')"
            :hint="$t('endpoint_exact_match_hint')"
            persistent-hint
            @keyup.enter.native="initiateSearch()"
            prepend-inner-icon="mdi-link-variant"/>
        </v-col>
        <v-col lg="6" md="12" cols="12">
          <div class="d-flex align-center">
            <date-picker
              v-model="searchCriteria.createdAfter"
              :timezone="customerTimezone"
              :label="$t('start_date')"
              prepend-inner-icon="mdi-calendar"
              reset-on-timezone-change/>
            <date-picker
              v-model="searchCriteria.createdBefore"
              :timezone="customerTimezone"
              :label="$t('end_date')"
              end-of-day
              prepend-inner-icon="mdi-calendar"
              reset-on-timezone-change/>
          </div>
        </v-col>
      </v-row>
      <div class="d-flex justify-end mr-3 mb-2">
        <v-btn outlined color="primary" @click="initiateSearch()" :loading="remoteData.isBusy">
          <v-icon class="mr-1 ml-n1">mdi-magnify</v-icon>
          {{ $t('search') }}
        </v-btn>
        <v-btn outlined color="primary" class="ml-2" @click="resetTable()" :disabled="tableControlsMatchDefaults">
          <v-icon class="mr-1 ml-n1">mdi-filter-remove</v-icon>
          {{ $t('reset') }}
        </v-btn>
      </div>
    </v-sheet>

    <div class="d-flex justify-space-between align-center mt-2">
      <div class="text-subtitle-1 font-weight-bold hidden-md-and-down">{{ $tc('result', 2) }}</div>
      <table-pagination-controls
        :total-rows="remoteData.totalRows"
        :current-page.sync="remoteData.currentPage"
        :rows-per-page.sync="remoteData.rowsPerPage"
        @update:currentPage="refreshResults"
        @update:rowsPerPage="initiateSearch"
        :per-page-options="remoteData.pageOptions"/>
    </div>

    <div class="data-table-wrapper" :class="{'loading': remoteData.isBusy}">
      <v-data-table
        hide-default-footer
        class="mt-2 vf-table-alt"
        :headers="tableFields"
        :items="remoteData.items"
        :server-items-length="remoteData.totalRows"
        :sort-by="remoteData.sortBy"
        :sort-desc="remoteData.sortDesc"
        :loading="remoteData.isBusy"
        :loading-text="$t('loading')"
        item-key="id"
        single-expand
        show-expand
        @update:sort-by="onSortByChange"
        @update:sort-desc="onSortDescChange">
        <template v-slot:item.created_at="row">
          <multi-time-display :time="row.item.createdAtIso"/>
        </template>
        <template v-slot:item.user="row">
          <div class="d-flex align-center justify-space-between">
            <span v-if="row.item.user_id != null">
              <router-link v-if="user.can('view_user', 'all')" :to="'/user/update/' + row.item.user_id">
                <v-icon color="primary">mdi-account</v-icon> {{ userLabel(row.item) }}
              </router-link>
              <span v-else>
                <v-icon>mdi-account</v-icon> {{ userLabel(row.item) }}
              </span>
            </span>
            <span v-else>
              <v-icon>mdi-server</v-icon> {{ $t('unauthenticated_request') }}
            </span>
            <v-btn
              v-if="row.item.user_id != null"
              fab
              text
              small
              color="std-icon-grey"
              class="hidden-lg-and-down"
              @click="filterByUser(row.item)">
              <v-icon>mdi-filter</v-icon>
            </v-btn>
          </div>
        </template>
        <template v-slot:item.customer="row">
          <if-set :value="row.item.customer" dash>
            <template v-slot:default="scope">
              <div class="d-flex align-center justify-space-between">
                <span>
                  <v-icon>mdi-office-building</v-icon>
                  {{ scope.value.name }}
                </span>
                <v-btn
                  fab
                  text
                  small
                  color="std-icon-grey"
                  class="hidden-lg-and-down"
                  @click="filterByCustomer(scope.value)">
                  <v-icon>mdi-filter</v-icon>
                </v-btn>
              </div>
            </template>
          </if-set>
        </template>
        <template v-slot:item.endpoint="row">
          <div class="d-flex align-center justify-space-between">
            <span class="endpoint-cell">{{ row.item.endpoint }}</span>
            <v-btn
              fab
              text
              small
              color="std-icon-grey"
              class="hidden-lg-and-down"
              @click="filterByEndpoint(row.item.endpoint)">
              <v-icon>mdi-filter</v-icon>
            </v-btn>
          </div>
        </template>
        <template v-slot:item.records_returned="row">
          <if-set :value="row.item.records_returned" dash/>
        </template>
        <template v-slot:item.response_time_ms="row">
          <if-set :value="row.item.response_time_ms" dash/>
        </template>
        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="grey lighten-4">
            <api-access-log-details :item="item"/>
          </td>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>

import { debounce, defaults, filter, isEmpty, map, sortBy, unionBy } from 'lodash'
import Moment from 'moment-timezone'
import DatePicker from '@/components/shared/DatePicker'
import IfSet from '@/components/shared/IfSet'
import MultiTimeDisplay from '@/components/shared/MultiTimeDisplay.vue'
import TablePaginationControls from '@/components/shared/TablePaginationControls'
import VfAutocomplete from '@/components/shared/VfAutocomplete'
import ApiAccessLogDetails from '@/components/api-access-log/ApiAccessLogDetails'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import RemoteDataTableMixin from '@/components/mixins/RemoteDataTableMixin'

/**
 * Filterable, paginated browser over the api_access_log table.
 *
 * Root only. The API endpoint answers 404 to any other caller, so the parent
 * view is responsible for not mounting this component for one.
 */
export default {
  name: 'ApiAccessLog',
  components: {
    ApiAccessLogDetails,
    DatePicker,
    IfSet,
    MultiTimeDisplay,
    TablePaginationControls,
    VfAutocomplete
  },
  mixins: [
    CurrentUserMixin,
    RemoteDataTableMixin
  ],
  props: {
    /**
     * Whether to run a search as soon as the component mounts.
     */
    autoload: {
      type: Boolean,
      default: true
    },
    /**
     * Search criteria to start from, overriding the defaults. Used by callers
     * that link into a pre-filtered browser.
     */
    initSearchCriteria: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      model: 'ApiAccessLog',
      searchCriteria: null,
      userOptions: [],
      loadingUserOptions: false
    }
  },
  computed: {
    tableFields () {
      return [
        // Only the three columns the API accepts a sort on are sortable. Every
        // one of those sorts is tie-broken on the primary key server side,
        // which is what keeps paging stable; offering a column the API does not
        // support would silently do nothing.
        {
          value: 'created_at',
          text: this.$t('timestamp')
        },
        {
          value: 'user',
          text: this.$tc('user'),
          sortable: false
        },
        {
          value: 'customer',
          text: this.$tc('customer'),
          sortable: false
        },
        {
          value: 'method',
          text: this.$t('http_method'),
          sortable: false
        },
        {
          value: 'endpoint',
          text: this.$t('endpoint'),
          sortable: false
        },
        {
          value: 'records_returned',
          text: this.$t('records_returned')
        },
        {
          value: 'response_time_ms',
          text: this.$t('response_time_ms')
        },
        {
          value: 'data-table-expand',
          text: this.$t('details'),
          sortable: false
        }
      ]
    },
    customerOptions () {
      return sortBy(
        map(this.customers, customer => ({ text: customer.name, value: customer.id })),
        'text'
      )
    },
    /**
     * The verbs the API stores. ApiAccessLog::HTTP_METHODS server side, plus the
     * OTHER placeholder it writes in place of a verb it does not recognize, so
     * those requests can still be found.
     */
    methodOptions () {
      return ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS', 'OTHER']
    }
  },
  methods: {
    /**
     * Returns the default search criteria for this data table.
     *
     * Defaults to today so that the first page is a bounded query. This table
     * takes hundreds of thousands of rows a day and the API applies no implicit
     * window of its own.
     *
     * @return Object
     */
    getDefaultSearchCriteria () {
      return defaults({}, this.initSearchCriteria, {
        userIds: [],
        customers: [],
        methods: [],
        endpoint: null,
        createdAfter: Moment().tz(this.customerTimezone).startOf('day').format(),
        createdBefore: null
      })
    },

    /**
     * Constructs the query parameters for an index request.
     *
     * @return Object
     */
    buildSearchCriteria () {
      const criteria = {
        // Everything the table and the expanded detail need, and nothing else.
        // Without this the expanded relations come back whole, which for the
        // user relation means shipping a full user record per row.
        fields: [
          'id',
          'user_id',
          'customer_id',
          'endpoint',
          'method',
          'query_params',
          'records_returned',
          'response_time_ms',
          'ip_address',
          'user_agent',
          'created_at',
          'user.id',
          'user.email',
          'customer.id',
          'customer.name'
        ].join(','),
        expand: 'user,customer',
        page: this.remoteData.currentPage,
        'per-page': this.remoteData.rowsPerPage
      }

      if (!isEmpty(this.searchCriteria.userIds)) {
        criteria.user_id = this.searchCriteria.userIds.join(',')
      }

      if (!isEmpty(this.searchCriteria.customers)) {
        criteria.customer_id = this.searchCriteria.customers.join(',')
      }

      if (!isEmpty(this.searchCriteria.methods)) {
        criteria.method = this.searchCriteria.methods.join(',')
      }

      if (this.searchCriteria.endpoint) {
        criteria.endpoint = this.searchCriteria.endpoint.trim()
      }

      if (this.searchCriteria.createdAfter) {
        criteria.created_after = this.searchCriteria.createdAfter
      }

      if (this.searchCriteria.createdBefore) {
        criteria.created_before = this.searchCriteria.createdBefore
      }

      // Truthiness, not a null check. Vuetify clears the sort by emitting
      // undefined, and a null check would let that through as the literal
      // string "sort=undefined". See onSortByChange().
      if (this.remoteData.sortBy) {
        criteria.sort = `${this.remoteData.sortDesc ? '-' : ''}${this.remoteData.sortBy}`
      }

      return criteria
    },

    /**
     * Records a sort column change and searches again.
     *
     * Vuetify's third click on a header removes the sort rather than cycling
     * back to ascending, and because sortBy is bound as a String rather than an
     * Array, VData emits sortBy[0] of an emptied array, which is undefined
     * (vuetify/lib/components/VData/VData.js line 234). Two things go wrong if
     * that undefined is stored as it arrives: buildSearchCriteria would send
     * sort=undefined, which the API ignores but the access logger dutifully
     * records, and RemoteDataTableMixin's tableControlsMatchDefaults compares
     * sortBy against null, so the Reset button would stay enabled for the rest
     * of the session. Normalize to the mixin's own resting values instead.
     *
     * The sort props are bound without .sync for the same reason: the generated
     * .sync handler and an explicit listener would both fire on this event, and
     * nothing guarantees which runs first.
     *
     * @param {String|undefined} sortBy
     */
    onSortByChange (sortBy) {
      this.remoteData.sortBy = sortBy === undefined ? null : sortBy
      this.initiateSearch()
    },

    /**
     * Records a sort direction change and searches again. Vuetify clears this
     * one to undefined too. See onSortByChange().
     *
     * @param {Boolean|undefined} sortDesc
     */
    onSortDescChange (sortDesc) {
      this.remoteData.sortDesc = sortDesc === undefined ? false : sortDesc
      this.initiateSearch()
    },

    /**
     * The email to show for a request's caller. The user relation is expanded on
     * every request, but the referenced user may since have been hard deleted,
     * in which case only the id survives.
     *
     * @param {Object} item
     * @return String
     */
    userLabel (item) {
      return item.user ? item.user.email : `#${item.user_id}`
    },

    /**
     * Filter to one row's caller and search again.
     *
     * @param {Object} item
     */
    filterByUser (item) {
      this.searchCriteria.userIds = [item.user_id]
      this.initiateSearch()
    },

    /**
     * Adds options to the caller autocomplete without discarding the ones a
     * current selection depends on for its label.
     *
     * The list is remote, so a selected id whose option is no longer present
     * would render as a bare number in its chip. Accumulating avoids that;
     * the autocomplete filters what it shows by the typed text anyway.
     *
     * @param {Array} options Objects with text and value keys.
     */
    rememberUserOptions (options) {
      this.userOptions = unionBy(this.userOptions, options, 'value')
    },

    /**
     * Filter to one row's customer and search again.
     *
     * @param {Object} customer
     */
    filterByCustomer (customer) {
      this.searchCriteria.customers = [customer.id]
      this.initiateSearch()
    },

    /**
     * Filter to one row's endpoint and search again.
     *
     * @param {String} endpoint
     */
    filterByEndpoint (endpoint) {
      this.searchCriteria.endpoint = endpoint
      this.initiateSearch()
    },

    /**
     * Harvests caller labels from the rows that just loaded.
     *
     * A deep link supplies user ids but no emails, so without this the chip for
     * a pre-applied caller filter would show a bare id until someone searched.
     * The rows already carry the email, so use it.
     *
     * (This uses the RemoteDataTableMixin transformRemoteData handle.)
     *
     * @param {Array} items
     * @return {Array}
     */
    transformRemoteData (items) {
      this.rememberUserOptions(
        map(
          filter(items, item => item.user_id != null && item.user),
          item => ({ text: item.user.email, value: item.user_id })
        )
      )

      return items
    },

    /**
     * Looks up users by email for the caller filter. Debounced, since it runs
     * on every keystroke in the autocomplete.
     *
     * @param {String} query
     */
    searchUsers: debounce(async function (query) {
      if (isEmpty(query)) {
        return
      }

      this.loadingUserOptions = true
      try {
        const collection = await this.$orm('User').findAll({ email: query })
        this.rememberUserOptions(map(collection.getItems(), item => ({ text: item.email, value: item.id })))
      } catch (error) {
        await this.$store.dispatch('globalMessagingModule/showModal', {
          message: this.$t('network_error_message')
        })
      } finally {
        this.loadingUserOptions = false
      }
    }, 300)
  },
  created () {
    this.searchCriteria = this.getDefaultSearchCriteria()
  },
  mounted () {
    if (this.autoload) {
      this.initiateSearch()
    }
  }
}
</script>

<style scoped lang="scss">
// Paths are long and have no spaces to wrap on.
.endpoint-cell {
  overflow-wrap: anywhere;
}
</style>
